import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createContact, listContacts, statement } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../components/neu/Neu";

export default function AcctContactsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "supplier", phone: "", trn: "" });
  const [sheet, setSheet] = useState(null);

  const load = useCallback(async () => setRows(await listContacts({})), []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    await createContact(form);
    toast.success(t("acct.contactCreated"));
    setForm({ name: "", kind: "supplier", phone: "", trn: "" });
    load();
  };

  return (
    <AcctShell titleKey="contacts">
      <NeuCard className="grid gap-3 p-5 sm:grid-cols-5" data-testid="contact-form">
        <NeuInput data-testid="contact-name" placeholder={t("acct.contactName")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <NeuSelect data-testid="contact-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          {["supplier", "customer", "both"].map((k) => (
            <option key={k} value={k}>{t(`acct.contactKind.${k}`)}</option>
          ))}
        </NeuSelect>
        <NeuInput data-testid="contact-phone" placeholder={t("acct.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <NeuInput data-testid="contact-trn" placeholder={t("acct.trn")} value={form.trn} onChange={(e) => setForm({ ...form, trn: e.target.value })} />
        <NeuButton variant="primary" data-testid="contact-save" disabled={!form.name} onClick={save}>
          {t("app.save")}
        </NeuButton>
      </NeuCard>

      <div className="grid gap-2" data-testid="contact-list">
        {rows.map((row) => (
          <NeuCard key={row.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`contact-row-${row.id}`}>
            <span className="min-w-0 flex-1 truncate text-sm font-bold">{row.name}</span>
            <span className="text-[11px] text-muted-foreground">{t(`acct.contactKind.${row.kind}`)}</span>
            <span className="font-mono text-sm font-bold" dir="ltr">{row.balance}</span>
            <NeuButton className="px-3 py-2 text-xs" data-testid={`contact-statement-${row.id}`} onClick={async () => setSheet(await statement(row.id, {}))}>
              {t("acct.statement")}
            </NeuButton>
          </NeuCard>
        ))}
      </div>

      {sheet ? (
        <NeuCard className="overflow-x-auto p-5" data-testid="statement-view">
          <p className="text-sm font-bold">{sheet.contact.name}</p>
          <p className="mt-1 font-mono text-xs" dir="ltr">
            {t("acct.opening")}: {sheet.opening_balance} · {t("acct.closing")}: {sheet.closing_balance}
          </p>
          <table className="mt-3 w-full min-w-[480px] text-xs">
            <tbody>
              {sheet.rows.map((r, i) => (
                <tr key={i} className="border-t border-border/40" data-testid={`statement-row-${i}`}>
                  <td className="p-2 font-mono" dir="ltr">{r.date}</td>
                  <td className="p-2 font-mono text-primary" dir="ltr">{r.number}</td>
                  <td className="p-2">{r.description}</td>
                  <td className="p-2 text-end font-mono" dir="ltr">{r.debit}</td>
                  <td className="p-2 text-end font-mono" dir="ltr">{r.credit}</td>
                  <td className="p-2 text-end font-mono font-bold" dir="ltr">{r.balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </NeuCard>
      ) : null}
    </AcctShell>
  );
}
